import type {
  EventProps,
  EventShipper,
  SetGroupsOptions,
  ShipperGroup,
  ShipperReplay,
  ShipperUser
} from '../shippers/types';
import type { EventCatalog, KnownEvent } from './events';

// Optional error sink. Defaults to `console.error`; wire `Sentry.captureException`
// (or any reporter) via `tracking.configure({ onError })` in apps that have one.
export type ErrorHandler = (error: unknown, context?: { shipper?: string }) => void;

const defaultOnError: ErrorHandler = (error, context) => {
  console.error('[observability] shipper error', context, error);
};

// Optional debug sink. When wired via `tracking.configure({ onEvent })`, it is
// called for every signal the dispatcher fans out, with the destinations it was
// sent to (ready shipper names, e.g. `faro`, `mixpanel`). Apps use this to power
// a "log all tracking events" dev toggle. No-op by default.
export type EventLogEntry =
  | { type: 'track'; name: string; props?: EventProps; shippers: string[] }
  | { type: 'measure'; name: string; value: number; props?: EventProps; shippers: string[] }
  | { type: 'identify'; user: ShipperUser; shippers: string[] }
  | { type: 'group'; groups: ShipperGroup[]; shippers: string[] }
  | { type: 'register'; props: EventProps; shippers: string[] }
  | { type: 'unregister'; keys: string[]; shippers: string[] }
  | { type: 'setSessionProps'; set: EventProps; drop: string[]; shippers: string[] }
  | { type: 'clear'; shippers: string[] }
  | { type: 'screenView'; path: string; props?: EventProps; shippers: string[] }
  | { type: 'revenue'; amount: number; props?: EventProps; shippers: string[] };

export type EventLogger = (entry: EventLogEntry) => void;

// Optional per-event property bag merged onto `track` / `measure` / `screenView`.
// Used to attach request- or session-scoped context (e.g. evaluated feature
// flags) without changing each call site. Explicit event props win on collision.
export type TrackEnricher = () => EventProps | undefined;

// Per-call dispatch options for `track`. `only` restricts the event to the named
// shippers (e.g. `['openpanel']`) instead of fanning out to every ready one —
// used to keep higher-volume events off limited-quota destinations like Mixpanel.
// `callback` is forwarded to each shipper's `trackEvent` (delivery completion).
export type TrackOptions = { only?: readonly string[]; callback?: () => void };

export type TrackingReplay = ShipperReplay & { shipper: string };

// Multi-destination event dispatcher. Every method fans out to each registered,
// ready shipper. Add a destination by calling `registerShipper(new MyShipper())`
// — no business-code change needed. Backend-agnostic: knows nothing about Faro,
// Mixpanel, or any app's user model beyond `ShipperUser`.
export class Tracking {
  private shippers: EventShipper[] = [];
  private onError: ErrorHandler = defaultOnError;
  private onEvent?: EventLogger;
  // Super properties split by lifetime. `bootProps` survive `clear()` (app,
  // environment, brand, browser). `sessionProps` are identity/session and drop
  // on logout so the next visitor is not tagged as the previous person.
  // Replayed onto shippers registered later (e.g. Mixpanel inits after Faro).
  private bootProps: EventProps = {};
  private sessionProps: EventProps = {};
  private currentUser?: ShipperUser;
  private currentGroups?: ShipperGroup[];
  // Groups flagged `{ boot: true }` (e.g. brand). Restored after `clear()` so
  // logout does not drop app context. Session groups (org / user_group) do not.
  private bootGroups: ShipperGroup[] = [];
  private enrichTrack?: TrackEnricher;
  private onClear?: () => void;

  // Inject an error reporter (e.g. Sentry) and/or a debug event logger. `onEvent`
  // gates itself, so wire it once and let it decide whether to emit (e.g. read a
  // dev-settings flag) — toggling does not need a re-configure.
  // `onClear` runs at the start of `clear()` so apps can reset session-scoped
  // claims (e.g. booking funnel) without wrapping `clear`.
  configure(options: {
    onError?: ErrorHandler;
    onEvent?: EventLogger;
    enrichTrack?: TrackEnricher;
    onClear?: () => void;
  }) {
    if (options.onError) {
      this.onError = options.onError;
    }
    if (options.onEvent) {
      this.onEvent = options.onEvent;
    }
    if (options.enrichTrack) {
      this.enrichTrack = options.enrichTrack;
    }
    if (options.onClear) {
      this.onClear = options.onClear;
    }
  }

  registerShipper(shipper: EventShipper) {
    this.shippers.push(shipper);
    // Backfill context set before this shipper registered.
    if (shipper.isReady()) {
      const superProps = this.allSuperProps();
      if (Object.keys(superProps).length) {
        this.safe(shipper, () => shipper.register?.(superProps));
      }
      if (this.currentUser) {
        this.safe(shipper, () => shipper.identify(this.currentUser!));
      }
      if (this.currentGroups?.length) {
        this.safe(shipper, () => shipper.setGroups?.(this.currentGroups!));
      }
    }
  }

  // Isolate failures: one broken shipper must never break tracking or the app.
  private safe(shipper: EventShipper, fn: () => void) {
    try {
      fn();
    } catch (error) {
      this.onError(error, { shipper: shipper.name });
    }
  }

  private ready(only?: readonly string[]) {
    const ready = this.shippers.filter((shipper) => shipper.isReady());
    return only ? ready.filter((shipper) => only.includes(shipper.name)) : ready;
  }

  // Merge enricher props under the call-site bag so explicit keys win. A throwing
  // enricher is reported and skipped — tracking itself must not fail the app.
  private enrichedProps(props?: EventProps): EventProps | undefined {
    const extra = this.safeEnrich();
    if (!extra || Object.keys(extra).length === 0) {
      return props;
    }
    return { ...extra, ...props };
  }

  private safeEnrich(): EventProps | undefined {
    try {
      return this.enrichTrack?.();
    } catch (error) {
      this.onError(error);
      return undefined;
    }
  }

  // Known events are type-checked against the catalog (augment `EventCatalog`
  // per app via declaration merging); free-form strings fall through to the
  // loose overload. The known overload MUST come first. Pass `options.only` to
  // restrict the event to specific shippers (e.g. OpenPanel-only events).
  // The third arg also accepts a bare callback for back-compat with the
  // pre-`TrackOptions` signature `track(name, props, callback)`.
  track<E extends KnownEvent>(name: E, props: EventCatalog[E], optionsOrCallback?: TrackOptions | (() => void)): void;
  track(name: string, props?: EventProps, optionsOrCallback?: TrackOptions | (() => void)): void;
  track(name: string, props?: EventProps, optionsOrCallback?: TrackOptions | (() => void)) {
    const options = typeof optionsOrCallback === 'function' ? { callback: optionsOrCallback } : optionsOrCallback;
    const merged = this.enrichedProps(props);
    const ready = this.ready(options?.only);
    ready.forEach((shipper) => this.safe(shipper, () => shipper.trackEvent(name, merged, options?.callback)));
    this.onEvent?.({ type: 'track', name, props: merged, shippers: ready.map((shipper) => shipper.name) });
  }

  // Numeric measurement (latency, duration, size). Fans out to shippers that
  // implement `measure`; others skip it.
  measure(name: string, value: number, props?: EventProps) {
    const merged = this.enrichedProps(props);
    const ready = this.ready();
    ready.forEach((shipper) => this.safe(shipper, () => shipper.measure?.(name, value, merged)));
    this.onEvent?.({ type: 'measure', name, value, props: merged, shippers: ready.map((shipper) => shipper.name) });
  }

  identify(user: ShipperUser) {
    this.currentUser = user;
    const ready = this.ready();
    ready.forEach((shipper) => this.safe(shipper, () => shipper.identify(user)));
    this.onEvent?.({ type: 'identify', user, shippers: ready.map((shipper) => shipper.name) });
  }

  // Associate the current profile with one or more groups (e.g. brand, service
  // department, organization, user group). Replaces membership for the owned
  // types only — other types already set stay. Pass `types` explicitly when
  // clearing (empty `groups`) so a partner department deselect does not wipe
  // org/user_group. Empty `groups` with no `types` clears current membership.
  // `{ boot: true }` keeps the owned types across `clear()` (logout).
  // Remembered and replayed onto shippers registered later.
  setGroups(groups: ShipperGroup[], options?: SetGroupsOptions) {
    const ownedTypes = options?.types ?? [...new Set(groups.map((group) => group.type))];
    this.currentGroups = nextGroups(this.currentGroups, groups, ownedTypes);
    if (options?.boot) {
      this.bootGroups = nextGroups(this.bootGroups, groups, ownedTypes);
    }
    const next = this.currentGroups ?? [];
    const ready = this.ready();
    ready.forEach((shipper) => this.safe(shipper, () => shipper.setGroups?.(next)));
    this.onEvent?.({ type: 'group', groups: next, shippers: ready.map((shipper) => shipper.name) });
  }

  // Per-user (people) property.
  setPeopleProperty(name: string, value: string | number | boolean) {
    this.ready().forEach((shipper) => this.safe(shipper, () => shipper.setUserProperty(name, value)));
  }

  clear() {
    this.onClear?.();
    const ready = this.ready();
    ready.forEach((shipper) => this.safe(shipper, () => shipper.clear?.()));
    this.onEvent?.({ type: 'clear', shippers: ready.map((shipper) => shipper.name) });
    // Drop remembered identity and session groups so re-registered shippers
    // don't replay the previous user. Boot groups (brand) survive like bootProps.
    this.currentUser = undefined;
    this.currentGroups = this.bootGroups.length ? [...this.bootGroups] : undefined;
    this.sessionProps = {};
    if (Object.keys(this.bootProps).length > 0) {
      ready.forEach((shipper) => this.safe(shipper, () => shipper.register?.(this.bootProps)));
    }
    if (this.bootGroups.length > 0) {
      ready.forEach((shipper) => this.safe(shipper, () => shipper.setGroups?.(this.bootGroups)));
    }
  }

  // OpenPanel Pages/Sessions. Only shippers that implement `screenView` receive
  // this (OpenPanel). Mixpanel is intentionally skipped — customer Mixpanel
  // page tracking stays on TrackScreen path-as-event-name. Identical-path
  // skipping lives in OpenPanelClientShipper (one HTTP request per event).
  screenView(path: string, props?: EventProps) {
    const merged = this.enrichedProps(props);
    const ready = this.ready().filter((shipper) => shipper.screenView);
    ready.forEach((shipper) => this.safe(shipper, () => shipper.screenView?.(path, merged)));
    this.onEvent?.({ type: 'screenView', path, props: merged, shippers: ready.map((shipper) => shipper.name) });
  }

  revenue(amount: number, props?: EventProps) {
    const ready = this.ready().filter((shipper) => shipper.revenue);
    ready.forEach((shipper) => this.safe(shipper, () => shipper.revenue?.(amount, props)));
    this.onEvent?.({ type: 'revenue', amount, props, shippers: ready.map((shipper) => shipper.name) });
  }

  // OpenPanel visitor device id for payment-session attribution. Undefined when
  // no OpenPanel shipper is ready (local stub-off, missing client id, etc.).
  getDeviceId(): string | undefined {
    for (const shipper of this.ready()) {
      const deviceId = shipper.getDeviceId?.();
      if (deviceId) {
        return deviceId;
      }
    }
    return undefined;
  }

  // Every recording in progress, tagged with the destination recording it, so
  // an error report can link to each replay and mark its timeline.
  getReplays(): TrackingReplay[] {
    return this.ready().flatMap((shipper) => {
      const replay = shipper.getReplay?.();
      return replay ? [{ shipper: shipper.name, ...replay }] : [];
    });
  }

  // Session super-props (identity flags). Dropped on `clear()`. Prefer
  // `setSessionProps` when registering and dropping related keys together.
  register(extra: EventProps) {
    this.dispatchRegister(this.sessionProps, extra);
  }

  unregister(keys: string[]) {
    const present = keys.filter((key) => key in this.sessionProps);
    if (present.length === 0) {
      return;
    }
    present.forEach((key) => {
      delete this.sessionProps[key];
    });
    const ready = this.ready();
    ready.forEach((shipper) => this.safe(shipper, () => shipper.unregister?.(present)));
    this.onEvent?.({ type: 'unregister', keys: present, shippers: ready.map((shipper) => shipper.name) });
  }

  // Atomically patch session super-props: register `set`, then drop keys in `drop`
  // (and any previous keys overwritten only when values change). Use for related
  // flags (e.g. impersonation) so callers never leave a half-applied bag.
  setSessionProps(update: { set?: EventProps; drop?: string[] }) {
    const set = update.set ?? {};
    const dropRequested = update.drop ?? [];
    const drop = dropRequested.filter((key) => key in this.sessionProps && !(key in set));
    const changed: EventProps = {};
    for (const [key, value] of Object.entries(set)) {
      if (this.sessionProps[key] !== value) {
        changed[key] = value;
      }
    }
    if (Object.keys(changed).length === 0 && drop.length === 0) {
      return;
    }
    Object.assign(this.sessionProps, changed);
    drop.forEach((key) => {
      delete this.sessionProps[key];
    });
    const ready = this.ready();
    if (Object.keys(changed).length > 0) {
      ready.forEach((shipper) => this.safe(shipper, () => shipper.register?.(changed)));
    }
    if (drop.length > 0) {
      ready.forEach((shipper) => this.safe(shipper, () => shipper.unregister?.(drop)));
    }
    this.onEvent?.({
      type: 'setSessionProps',
      set: changed,
      drop,
      shippers: ready.map((shipper) => shipper.name)
    });
  }

  // Boot super-props (app, environment, brand, browser). Survive `clear()` and
  // are re-applied after destinations wipe state.
  registerBoot(extra: EventProps) {
    this.dispatchRegister(this.bootProps, extra);
  }

  private allSuperProps(): EventProps {
    return { ...this.bootProps, ...this.sessionProps };
  }

  private dispatchRegister(target: EventProps, extra: EventProps) {
    const keys = Object.keys(extra);
    if (keys.length > 0 && keys.every((key) => target[key] === extra[key])) {
      return;
    }
    Object.assign(target, extra);
    const ready = this.ready();
    ready.forEach((shipper) => this.safe(shipper, () => shipper.register?.(extra)));
    this.onEvent?.({ type: 'register', props: extra, shippers: ready.map((shipper) => shipper.name) });
  }
}

function nextGroups(
  current: ShipperGroup[] | undefined,
  groups: ShipperGroup[],
  ownedTypes: readonly string[]
): ShipperGroup[] {
  if (ownedTypes.length === 0) {
    return [];
  }
  const kept = (current ?? []).filter((group) => !ownedTypes.includes(group.type));
  return [...kept, ...groups];
}

// Shared singleton — the common entry point for app code.
export const tracking = new Tracking();
